import { BookingsGrid } from "@collins/react-componments";
import { Booking } from "@collins/react-componments/dist/types/booking";
import {
  OnBookingChangeEventType,
  OnDragModeToggledEventType,
  OnGridClickEventType,
  OnShowBookingClickEventType
} from "@collins/react-componments/dist/types/eventHandlers";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ViewBooking } from "collins-js-sdk";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import { fetchBookingV4FromAPI } from "../../../../APIs/Booking/GetBooking";
import { WEEKDAY_KEYS } from "../../../../constants";

import {
  addOrUpdateBooking,
  mapBookingV4ToState
} from "../../../../APIs/Booking/UpdateBooking";

import {
  deleteClosedArea,
  updateClosedArea
} from "../../../../APIs/Venue/ClosedArea";
import { updateVenue } from "../../../../APIs/Venue/UpdateVenue";
import { useDiaryContext } from "../../../../hooks/useDiaryContext";
import { useNotificationsContext } from "../../../../hooks/useNotificationsContext";
import { useSelectedBookingAPIData } from "../../../../hooks/useSelectedBookingAPIData";
import { useShowAICoPilot } from "../../../../hooks/useShowAICoPilot";
import { useUserContext } from "../../../../hooks/useUserContext";
import { bookableAreasClosed } from "../../../../types/venues";
import { getBookingsForGrid, getCustomerName } from "../../../../utils/booking";
import {
  removeZonesFromArea,
  sortZonesByAreaOrder
} from "../../../../utils/zoneOrdering";
import { CloseAreasModal } from "../CloseAreasModal/CloseAreasModal";
import { CreateEditClosedArea } from "../CreateEditClosedArea/CreateEditClosedArea";
import FloorPlan from "../Floorplan/FloorPlan";
import { RunsheetBeta } from "../Runsheet/RunsheetBeta";
import EditBooking from "../SelectedBooking/EditBooking/EditBooking";
import SelectedBooking from "../SelectedBooking/SelectedBooking";
import {
  clearAllHighlightedAreas,
  toggleHighlightArea
} from "../Sidebar/AddBooking/AddBookingController";
import { ProactivePatternBanner } from "../SpaceScanner/AIInsights/ProactivePatternBanner";
import { ProactiveSuggestion } from "../SpaceScanner/AIInsights/ProactiveSuggestion";
import SubHeader from "../SubHeader/SubHeader";

type SortOrder = "none" | "asc" | "desc";

const DiaryMain = () => {
  useSelectedBookingAPIData();
  const diaryContext = useDiaryContext();
  const {
    // isInitialising,
    venueConfig,
    gridConfig,
    bookingsFiltered,
    date,
    selectedBooking,
    selectedTable,
    selectedView,
    stages,
    editBookingId,
    selectedBookingId,
    selectedVenue,
    openedFromEnquiry,
    setDiaryContext
  } = diaryContext;
  const { user } = useUserContext();
  const notificationsContext = useNotificationsContext();
  const queryClient = useQueryClient();
  const [bookingsLocal, setBookingsLocal] = useState<Booking[]>();
  const [sortOrder, setSortOrder] = useState<SortOrder>("none");
  const [closeAreasModalVisible, setCloseAreasModalVisible] =
    useState<boolean>(false);
  const dragModeToggled = React.useRef<boolean>(false);
  const [createEditClosedAreaModalOpen, setCreateEditClosedAreaModalOpen] =
    useState<boolean>(false);
  const [selectedClosureForEdit, setSelectedClosureForEdit] = useState<
    bookableAreasClosed | undefined
  >(undefined);
  const [localClosures, setLocalClosures] = useState<bookableAreasClosed[]>([]);
  const isAICoPilotEnabled = useShowAICoPilot();
  const openSpaceOptimiserRef = useRef<(() => void) | null>(null);

  const handleRegisterOpenSpaceOptimiser = useCallback((open: () => void) => {
    openSpaceOptimiserRef.current = open;
  }, []);

  const handleOpenSpaceOptimiser = useCallback(() => {
    openSpaceOptimiserRef.current?.();
  }, []);
  const [openedFromCloseAreasModal, setOpenedFromCloseAreasModal] =
    useState<boolean>(false);

  const { data: bookingV4ForEdit } = useQuery({
    queryKey: ["getBookingForEdit", editBookingId],
    queryFn: () =>
      editBookingId ? fetchBookingV4FromAPI(editBookingId) : undefined,
    enabled: !!editBookingId
  });

  // Fetch booking for SelectedBooking view when navigating from /bookings/:bookingId
  const { data: bookingV4ForSelect } = useQuery({
    queryKey: ["getBookingForSelect", selectedBookingId],
    queryFn: () =>
      selectedBookingId ? fetchBookingV4FromAPI(selectedBookingId) : undefined,
    enabled: !!selectedBookingId && !selectedBooking
  });

  // Set selectedBooking when booking data is fetched from URL navigation
  useEffect(() => {
    if (bookingV4ForSelect && selectedBookingId && !selectedBooking) {
      const bookingForSelect = mapBookingV4ToState(bookingV4ForSelect);
      setDiaryContext({
        selectedBooking: bookingForSelect,
        selectedBookingV4: bookingV4ForSelect
      });
    }
  }, [bookingV4ForSelect, selectedBookingId, selectedBooking, setDiaryContext]);

  const bookingForEdit = bookingV4ForEdit
    ? mapBookingV4ToState(bookingV4ForEdit)
    : undefined;

  const handleCloseEdit = useCallback(() => {
    setDiaryContext({ editBookingId: undefined });
  }, [setDiaryContext]);

  const handleEditClosure = useCallback((closure: bookableAreasClosed) => {
    setSelectedClosureForEdit(closure);
    setCreateEditClosedAreaModalOpen(true);
    setOpenedFromCloseAreasModal(true);
    setCloseAreasModalVisible(false);
  }, []);

  const handleAddClosure = useCallback(() => {
    setSelectedClosureForEdit(undefined);
    setCreateEditClosedAreaModalOpen(true);
    setOpenedFromCloseAreasModal(true);
    setCloseAreasModalVisible(false);
  }, []);

  const handleClosureUpdate = useCallback((closure: bookableAreasClosed) => {
    setLocalClosures((prev) => {
      const existingIndex = prev.findIndex((c) => c.id === closure.id);
      if (existingIndex >= 0) {
        const updated = [...prev];
        updated[existingIndex] = closure;
        return updated;
      } else {
        return [...prev, closure];
      }
    });
  }, []);

  const handleClosureDelete = useCallback((closure: bookableAreasClosed) => {
    setLocalClosures((prev) => prev.filter((c) => c.id !== closure.id));
  }, []);

  const handleCloseCreateEdit = useCallback(() => {
    setCreateEditClosedAreaModalOpen(false);
    setSelectedClosureForEdit(undefined);

    if (openedFromCloseAreasModal) {
      setCloseAreasModalVisible(true);
      setOpenedFromCloseAreasModal(false);
    }
  }, [openedFromCloseAreasModal]);

  const handleClosedAreasSave = useCallback(async () => {
    if (!venueConfig?._id) {
      notificationsContext.addNotification({
        heading: "Unable to save: Venue not found",
        autoClose: 3000,
        toastType: "error"
      });
      return;
    }

    const result = await updateVenue({
      venueId: venueConfig._id,
      bookableAreasClosed: localClosures
    });

    if (result) {
      notificationsContext.addNotification({
        heading: "Closed areas saved successfully",
        autoClose: 2000,
        toastType: "success"
      });

      queryClient.invalidateQueries({
        queryKey: ["venueConfig", selectedVenue]
      });

      setCloseAreasModalVisible(false);
    } else {
      notificationsContext.addNotification({
        heading: "Failed to save closed areas",
        autoClose: 3000,
        toastType: "error"
      });
    }
  }, [
    venueConfig,
    localClosures,
    notificationsContext,
    queryClient,
    selectedVenue
  ]);

  useEffect(() => {
    if (selectedView === "DiaryTab" && selectedTable) {
      clearAllHighlightedAreas();
      toggleHighlightArea(selectedTable.id);
    }
  }, [selectedView, selectedTable, selectedBooking]);

  useEffect(() => {
    setLocalClosures(venueConfig?.bookable_areas_closed ?? []);
  }, [venueConfig?.bookable_areas_closed]);

  const activeClosuresCount = useMemo(() => {
    if (!localClosures || !date) return 0;

    const currentDate = new Date(date);
    const currentDay = WEEKDAY_KEYS[(currentDate.getDay() + 6) % 7]; // Convert Sunday=0 to Monday=0

    return localClosures.filter((closure) => {
      if (closure.date_type === "weekdays" && closure.weekdays) {
        return closure.weekdays[currentDay as keyof typeof closure.weekdays];
      }

      if (closure.date_from && closure.date_to) {
        const dateFrom = new Date(closure.date_from);
        const dateTo = new Date(closure.date_to);
        dateFrom.setHours(0, 0, 0, 0);
        dateTo.setHours(23, 59, 59, 999);
        const currentDateStart = new Date(currentDate);
        currentDateStart.setHours(0, 0, 0, 0);
        return currentDateStart >= dateFrom && currentDateStart <= dateTo;
      }

      return false;
    }).length;
  }, [localClosures, date]);

  const handleOpenCloseAreasModal = useCallback(() => {
    setCloseAreasModalVisible(true);
  }, []);

  const handleCloseCloseAreasModal = useCallback(() => {
    setCloseAreasModalVisible(false);
  }, []);

  const onClosureClick = useCallback((closure: bookableAreasClosed) => {
    setCreateEditClosedAreaModalOpen(true);
    setSelectedClosureForEdit(closure);
    setOpenedFromCloseAreasModal(false);
  }, []);

  const handleUpdateClosedArea = useCallback(
    async (closure: bookableAreasClosed) => {
      if (!venueConfig?._id) {
        notificationsContext.addNotification({
          heading: "Unable to save: Venue not found",
          autoClose: 3000,
          toastType: "error"
        });
        return;
      }

      const result = await updateClosedArea({
        venueId: venueConfig._id,
        closedArea: closure
      });

      if (result) {
        notificationsContext.addNotification({
          heading: "Closed area saved successfully",
          autoClose: 2000,
          toastType: "success"
        });

        queryClient.invalidateQueries({
          queryKey: ["venueConfig", selectedVenue]
        });
      } else {
        notificationsContext.addNotification({
          heading: "Failed to save closed area",
          autoClose: 3000,
          toastType: "error"
        });
      }
    },
    [venueConfig, notificationsContext, queryClient, selectedVenue]
  );

  const handleDeleteClosedArea = useCallback(
    async (closure: bookableAreasClosed) => {
      if (!venueConfig?._id || !closure.id) {
        notificationsContext.addNotification({
          heading: "Unable to delete: Venue or closure not found",
          autoClose: 3000,
          toastType: "error"
        });
        return;
      }

      const success = await deleteClosedArea({
        venueId: venueConfig._id,
        closedAreaId: closure.id
      });

      if (success) {
        notificationsContext.addNotification({
          heading: "Closed area deleted successfully",
          autoClose: 2000,
          toastType: "success"
        });

        queryClient.invalidateQueries({
          queryKey: ["venueConfig", selectedVenue]
        });

        setCreateEditClosedAreaModalOpen(false);
        setSelectedClosureForEdit(undefined);
      } else {
        notificationsContext.addNotification({
          heading: "Failed to delete closed area",
          autoClose: 3000,
          toastType: "error"
        });
      }
    },
    [venueConfig, notificationsContext, queryClient, selectedVenue]
  );

  const siteConfigProps = useMemo(
    () => ({
      areas: gridConfig.displayZones
        ? (venueConfig?.bookable_areas ?? [])
        : removeZonesFromArea(venueConfig?.bookable_areas),
      stages,
      zones: sortZonesByAreaOrder(
        venueConfig?.bookable_area_zones ?? [],
        venueConfig?.bookable_areas ?? []
      ),
      types: venueConfig?.booking_types ?? [],
      areaClosedRules: venueConfig?.bookable_areas_closed ?? [],
      openingTimes: venueConfig?.opening_times
    }),
    [venueConfig, stages, gridConfig.displayZones]
  );

  const onBookingChange = useCallback(
    (e: CustomEvent<OnBookingChangeEventType>) => {
      const booking = e.detail.entity as ViewBooking;

      addOrUpdateBooking({
        booking: {
          ...booking,
          ...(e.detail.type === "area" ? { additionalAssignedAreas: null } : {})
        },
        diaryContext
      });
    },
    [addOrUpdateBooking, diaryContext]
  );

  const onDragModeToggled = useCallback(
    (e: CustomEvent<OnDragModeToggledEventType>) => {
      if (dragModeToggled.current === e.detail.value) {
        return;
      }
      const msg = e.detail.value
        ? "Grid unlocked. Bookings can be moved."
        : "Grid locked. Bookings can not be moved.";
      notificationsContext.addNotification({
        heading: msg,
        autoClose: 2000,
        toastType: "information"
      });
      dragModeToggled.current = e.detail.value;
    },
    [notificationsContext.addNotification]
  );

  const onShowBookingClick = useCallback(
    (e: CustomEvent<OnShowBookingClickEventType>) => {
      const selectedBooking = e.detail.entity as ViewBooking;
      setDiaryContext({ selectedBooking });
    },
    [setDiaryContext]
  );

  const onGridClick = useCallback(
    (e: CustomEvent<OnGridClickEventType>) => {
      if (!e.detail.newValue.area.id) {
        return;
      }

      const area =
        venueConfig?.bookable_areas?.find(
          (area) => area.id === e.detail.newValue.area.id
        ) ?? e.detail.newValue.area;

      const areaID = e.detail.newValue.area.id;

      const addModal = document.getElementsByClassName(
        "DiaryAddBookingSection"
      );

      if (e.detail.type === "single" && addModal.length === 0) {
        clearAllHighlightedAreas();

        if (areaID === selectedTable?.id) {
          setDiaryContext({
            selectedTable: undefined
          });

          return;
        }

        setDiaryContext({
          selectedTable: {
            name: area?.name ?? "",
            id: areaID
          }
        });

        toggleHighlightArea(areaID);

        return;
      }

      if (e.detail.type === "double") {
        clearAllHighlightedAreas();
      }

      toggleHighlightArea(areaID);

      // delay the state update, without this the DOM was waiting for state update to finish before applying highlight
      setTimeout(() => {
        setDiaryContext({
          gridAreaClick: {
            ...e.detail,
            newValue: { ...e.detail.newValue, area }
          }
        });
      }, 150);
    },
    [selectedTable, venueConfig]
  );

  useEffect(() => {
    setBookingsLocal(getBookingsForGrid(bookingsFiltered));
  }, [bookingsFiltered]);

  const sortedBookings = useMemo(() => {
    if (!bookingsFiltered || sortOrder === "none") {
      return bookingsFiltered ?? [];
    }

    const sorted = [...bookingsFiltered].sort(
      (a: ViewBooking, b: ViewBooking) => {
        const nameA = getCustomerName(a).toLowerCase();
        const nameB = getCustomerName(b).toLowerCase();

        if (nameA < nameB) return sortOrder === "asc" ? -1 : 1;
        if (nameA > nameB) return sortOrder === "asc" ? 1 : -1;
        return 0;
      }
    );

    return sorted;
  }, [bookingsFiltered, sortOrder]);

  const handleSortToggle = () => {
    setSortOrder((current) => {
      if (current === "none") return "asc";
      if (current === "asc") return "desc";
      return "none";
    });
  };

  return (
    <>
      {(selectedBooking || selectedBookingId || openedFromEnquiry) && (
        <SelectedBooking />
      )}
      {editBookingId && bookingForEdit && venueConfig && (
        <EditBooking
          booking={bookingForEdit}
          venueConfig={venueConfig}
          handleClose={handleCloseEdit}
        />
      )}

      {closeAreasModalVisible && (
        <CloseAreasModal
          venueConfig={venueConfig}
          date={date}
          closures={localClosures}
          onEdit={handleEditClosure}
          onAddAnother={handleAddClosure}
          onDelete={handleClosureDelete}
          onSave={handleClosedAreasSave}
          onClose={handleCloseCloseAreasModal}
        />
      )}

      <CreateEditClosedArea
        open={createEditClosedAreaModalOpen}
        selectedClosedArea={selectedClosureForEdit}
        venueConfig={venueConfig}
        onSave={
          openedFromCloseAreasModal
            ? handleClosureUpdate
            : handleUpdateClosedArea
        }
        onClose={handleCloseCreateEdit}
        onDelete={handleDeleteClosedArea}
        showDeleteButton={!openedFromCloseAreasModal}
      />

      <div
        style={{
          // be careful changing these styles, they affect the diary rendering (click booking scroll to center)
          display:
            selectedBooking || selectedBookingId || openedFromEnquiry
              ? "none"
              : "unset",
          height: "100%",
          width: "100%"
        }}
      >
        {/* flex + overflow-hidden enables flex containment for all tabs.
            Floorplan and Diary tabs set their own explicit h-[calc(...)] so they are unaffected.
            Runsheet relies on flex-1 + min-h-0 to fill remaining space. */}
        <div className="DiaryMainContentContainer flex flex-col h-full overflow-hidden">
          <SubHeader
            closedAreasCount={activeClosuresCount}
            onCloseAreasClick={handleOpenCloseAreasModal}
            closeAreasModalVisible={closeAreasModalVisible}
            onRegisterOpenSpaceOptimiser={
              isAICoPilotEnabled ? handleRegisterOpenSpaceOptimiser : undefined
            }
          />
          {isAICoPilotEnabled && (
            <ProactiveSuggestion
              bookings={bookingsFiltered ?? []}
              areas={venueConfig?.bookable_areas ?? []}
              date={date}
              onRunSpaceOptimiser={handleOpenSpaceOptimiser}
            />
          )}
          {isAICoPilotEnabled && selectedVenue && (
            <ProactivePatternBanner
              venueId={selectedVenue}
              onRunSpaceOptimiser={handleOpenSpaceOptimiser}
            />
          )}
          {selectedView === "RunsheetTab" && (
            <div className="BookingsGridContainer RunsheetContainer flex flex-col flex-1 overflow-hidden min-h-0">
              <RunsheetBeta
                sortedBookings={sortedBookings}
                sortOrder={sortOrder}
                onSortToggle={handleSortToggle}
              />
            </div>
          )}
          {selectedView === "FloorplanTab" && (
            <div
              className="BookingsGridContainer flex flex-row flex-[1_100%] overflow-auto overscroll-none border-0 border-solid pb-[var(--safe-area-inset-bottom,0px)] h-[calc(100vh-140px-var(--workspace-tab-adjustment,0px)-var(--safe-area-inset-top,0px))] min-h-[calc(100vh-140px-var(--workspace-tab-adjustment,0px)-var(--safe-area-inset-top,0px))]"
              style={{
                display: selectedView !== "FloorplanTab" ? "none" : "flex"
              }}
            >
              <FloorPlan />
            </div>
          )}
          {selectedView === "DiaryTab" && (
            <div
              className="BookingsGridContainer flex flex-row flex-[1_100%] overflow-auto overscroll-none border-0 border-solid pb-[var(--safe-area-inset-bottom,0px)] h-[calc(100vh-140px-var(--workspace-tab-adjustment,0px)-var(--safe-area-inset-top,0px))] min-h-[calc(100vh-140px-var(--workspace-tab-adjustment,0px)-var(--safe-area-inset-top,0px))]"
              style={{
                display: selectedView !== "DiaryTab" ? "none" : "flex"
              }}
            >
              <BookingsGrid
                bookings={bookingsLocal ?? []}
                date={date}
                gridConfig={gridConfig}
                siteConfig={siteConfigProps}
                onBookingChange={onBookingChange}
                onDragModeToggled={onDragModeToggled}
                onShowBookingClick={onShowBookingClick}
                onGridClick={onGridClick}
                onClosureClick={onClosureClick}
                locale={user?.locale}
              />
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default DiaryMain;
